import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Phone, Clock, Store, Navigation, Handshake, Check } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { locations as mockLocations, images } from '../mock/mock';
import { useContent } from '../context/ContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// almond-shaped map pin
const almondSvg = `<svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg"><defs><filter id="alnshadow" x="-40%" y="-25%" width="180%" height="160%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/></filter></defs><g filter="url(#alnshadow)"><path d="M17 43C17 43 31 27 31 16A14 14 0 1 0 3 16C3 27 17 43 17 43Z" fill="#2C3A28"/><path d="M17 6.6C14 8.1 11.6 11.7 11.6 16.4 11.6 21.2 14 24.8 17 24.8s5.4-3.6 5.4-8.4C22.4 11.7 20 8.1 17 6.6z" fill="#D2B489"/><g stroke="#2C3A28" stroke-width="0.8" fill="none" opacity="0.45"><path d="M17 9.6c-.8 3.7-1 8.2-.5 11.8"/><path d="M18.5 10.6c-.3 3.2-.3 6.6.1 9.8"/><path d="M15.4 10.7c-.5 3-.5 6.1-.1 9"/></g></g></svg>`;

const makeIcon = (isActive) =>
  L.divIcon({
    className: 'alnat-marker',
    html: `<div style="transform:scale(${isActive ? 1.3 : 1});transform-origin:bottom center;transition:transform .2s ease">${almondSvg}</div>`,
    iconSize: [34, 44],
    iconAnchor: [17, 43],
    popupAnchor: [0, -38],
  });

const toRad = (d) => (d * Math.PI) / 180;
const distKm = (a, b) => {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export default function WhereToBuy() {
  const { content, txt } = useContent();
  const locations = useMemo(
    () => (content.stockists && content.stockists.length ? content.stockists : mockLocations),
    [content.stockists]
  );
  const [active, setActive] = useState(mockLocations[0]);
  useEffect(() => { setActive(locations[0]); }, [locations]);
  const [form, setForm] = useState({ name: '', business: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  useReveal([]);

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const listRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markersRef.current = {}; }
    if (!mapEl.current) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView([-37.84, 144.97], 12);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    const bounds = [];
    locations.forEach((loc) => {
      const marker = L.marker([loc.lat, loc.lng], { icon: makeIcon(loc.id === locations[0].id) })
        .addTo(map)
        .bindPopup(`<strong>${loc.name}</strong><br/>${loc.address}`);
      marker.on('click', () => {
        setActive(loc);
        map.flyTo([loc.lat, loc.lng], Math.max(map.getZoom(), 14), { duration: 0.6 });
        marker.openPopup();
      });
      markersRef.current[loc.id] = marker;
      bounds.push([loc.lat, loc.lng]);
    });

    const t = setTimeout(() => {
      if (mapRef.current !== map) return;
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [55, 55], animate: false });
    }, 120);

    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, [locations]);

  useEffect(() => {
    if (!mapRef.current) return;
    Object.entries(markersRef.current).forEach(([id, m]) => m.setIcon(makeIcon(id === active.id)));
    const list = listRef.current;
    if (list) {
      const tile = list.querySelector(`[data-loc-id="${active.id}"]`);
      if (tile) tile.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [active]);

  const selectLocation = (loc) => {
    setActive(loc);
    const map = mapRef.current;
    if (!map) return;
    map.flyTo([loc.lat, loc.lng], Math.max(map.getZoom(), 14), { duration: 0.6 });
    const m = markersRef.current[loc.id];
    if (m) m.openPopup();
  };

  const submitWholesale = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        business: form.business,
        message: form.message,
        type: 'wholesale',
      });
      setSent(true);
      setForm({ name: '', business: '', email: '', message: '' });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('wholesale enquiry failed', err);
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
        <img
          src={images.whereHeader}
          alt="Luna Park, St Kilda"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            Where to buy
          </span>
          <h1 className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            Find us near you
          </h1>
          <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
            Visit our flagship café in St Kilda or pick up a bottle from one of our
            trusted stockists across Melbourne.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-8">
          {/* list */}
          <div ref={listRef} className="space-y-4 lg:max-h-[620px] lg:overflow-y-auto lg:pr-2 alnat-scroll">
            {locations.map((loc) => {
              const isActive = loc.id === active.id;
              return (
                <button
                  key={loc.id}
                  data-loc-id={loc.id}
                  onClick={() => selectLocation(loc)}
                  className={`reveal is-visible w-full text-left rounded-3xl p-6 border transition-all ${
                    isActive
                      ? 'bg-[#2C3A28] text-[#EDEAE0] border-[#2C3A28] shadow-xl'
                      : 'bg-[#EDEAE0] text-[#2C3A28] border-transparent hover:border-[#2C3A28]/40 hover:-translate-y-0.5'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                        loc.type === 'Flagship Café'
                          ? 'bg-[#2C3A28] text-white'
                          : isActive
                          ? 'bg-[#EDEAE0]/20 text-[#EDEAE0]'
                          : 'bg-[#2C3A28]/20 text-[#5C6738]'
                      }`}
                    >
                      {loc.type === 'Flagship Café' ? <Store size={12} /> : <MapPin size={12} />}
                      {loc.type}
                    </span>
                  </div>
                  <div className="mt-3 flex items-baseline justify-between gap-2">
                    <h3 className="font-display text-2xl font-semibold">{loc.name}</h3>
                    {loc.id !== locations[0].id && (
                      <span className={`shrink-0 text-xs font-semibold ${isActive ? 'text-[#2C3A28]' : 'text-[#3B2A20]/50'}`}>
                        {distKm(locations[0], loc).toFixed(1)} km
                      </span>
                    )}
                  </div>
                  <div className={`mt-3 space-y-2 text-sm ${isActive ? 'text-[#EDEAE0]/80' : 'text-[#3B2A20]/70'}`}>
                    <p className="flex items-start gap-2">
                      <MapPin size={15} className="mt-0.5 shrink-0 text-[#2C3A28]" /> {loc.address}
                    </p>
                    {loc.phone && (
                      <p className="flex items-center gap-2">
                        <Phone size={15} className="text-[#2C3A28]" /> {loc.phone}
                      </p>
                    )}
                    {loc.hours && (
                      <p className="flex items-center gap-2">
                        <Clock size={15} className="text-[#2C3A28]" /> {loc.hours}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* map */}
          <div className="reveal is-visible">
            <div
              ref={mapEl}
              data-testid="store-map"
              className="rounded-3xl overflow-hidden shadow-xl border border-[#2C3A28]/10 h-[420px] lg:h-[620px] bg-[#EDEAE0] z-0 isolate"
            />
            <div className="mt-4 flex items-center justify-between rounded-2xl bg-[#EDEAE0] px-6 py-4">
              <div>
                <p className="font-display text-xl text-[#2C3A28]">{active.name}</p>
                <p className="text-sm text-[#3B2A20]/60">{active.address}</p>
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${active.lat},${active.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-5 py-3 font-semibold hover:bg-[#2C3A28] transition-colors"
              >
                <Navigation size={16} /> Directions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* wholesale */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal is-visible rounded-[2rem] bg-[#2C3A28] text-[#EDEAE0] px-8 sm:px-14 py-14 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center overflow-hidden relative grain">
            <div className="relative">
              <Handshake size={38} className="text-[#EDEAE0]" />
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">{txt('whereToBuy', 'wholesaleHeading', 'Wholesale & partnerships')}</h2>
              <p className="mt-4 max-w-xl text-white text-lg leading-relaxed">
                {txt('whereToBuy', 'wholesaleBody', 'Run a café, grocer or restaurant? Stock the freshest nut milks in Australia. We work hand-in-hand with local partners who care about quality as much as we do.')}
              </p>
            </div>
            <div className="relative">
              {sent ? (
                <div className="rounded-2xl bg-[#E9EBED] text-[#2C3A28] p-8 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-[#2C3A28] text-[#E9EBED] flex items-center justify-center">
                    <Check size={24} />
                  </div>
                  <p className="mt-4 font-display text-2xl">Grazie!</p>
                  <p className="mt-1 text-[#3B2A20]/70">Thanks for reaching out, we'll be in touch soon.</p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-5 text-sm font-semibold text-[#2C3A28] hover:text-[#2C3A28]"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submitWholesale} className="rounded-2xl bg-[#E9EBED] p-6 sm:p-7 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                    />
                    <input
                      value={form.business}
                      onChange={(e) => setForm({ ...form, business: e.target.value })}
                      placeholder="Business name"
                      className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                    />
                  </div>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address"
                    className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                  />
                  <textarea
                    required
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your venue..."
                    className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors resize-none"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full rounded-full bg-[#2C3A28] text-[#EDEAE0] py-3.5 font-semibold hover:bg-[#1f2a1c] transition-colors disabled:opacity-60"
                  >
                    {busy ? 'Sending...' : 'Become a stockist'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
